import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import ReCAPTCHA from "react-google-recaptcha";
import SimpleReactValidator from "simple-react-validator";
import { toast } from "react-toastify";
import "./QuoteModal.css";

// Services from MegaMenu
const SERVICES = [
  { group: "AI", items: ["AI Agents", "Generative AI", "AI Driven Web Scraping", "Machine Learning"] },
  { group: "Digital Transformation", items: ["Legacy Modernization"] },
  { group: "Professional Staffing", items: ["Contract Hiring"] },
  { group: "Web Development", items: ["Web Portal Design & Development", "System Integration", "Ecommerce Website", "Business Websites"] },
  { group: "Branding & Identity", items: ["Branding"] },
  { group: "Data Migration", items: ["Cloud Migration", "Email Migration"] },
  { group: "Other Services", items: ["Mobile App Development", "AR/VR Development", "Graphic Designing", "Website/Software Maintenance"] },
];

const BUDGET_OPTIONS = [
  "Under ₹50,000",
  "₹50,000 - ₹1,00,000",
  "₹1,00,000 - ₹5,00,000",
  "₹5,00,000 - ₹10,00,000",
  "Above ₹10,00,000",
  "Not Sure / Discuss Later",
];

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QuoteModal: React.FC<QuoteModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    service: "",
    budget: "",
    message: "",
  });

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [, forceUpdate] = useState({});
  
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [validator] = useState(
    new SimpleReactValidator({
      className: "text-danger",
      messages: {
        required: "This field is required",
        email: "Please enter a valid email address",
        numeric: "Please enter a valid phone number",
      },
    })
  );

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open (works with Lenis)
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validator.allValid()) {
      if (!recaptchaToken) {
        toast.error("Please complete the reCAPTCHA verification");
        return;
      }

      setIsSubmitting(true);
      
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${apiUrl}/api/quote`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            recaptchaToken,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setIsSuccess(true);
          
          // Reset form
          setFormData({
            name: "",
            email: "",
            countryCode: "+91",
            phone: "",
            service: "",
            budget: "",
            message: "",
          });
          setRecaptchaToken(null);
          recaptchaRef.current?.reset();
          validator.hideMessages();
        } else {
          toast.error(data.message || "Failed to submit quote request");
        }
      } catch (error) {
        console.error("Submission error:", error);
        toast.error("Something went wrong. Please try again later.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      validator.showMessages();
      forceUpdate({});
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const resetAndClose = () => {
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="quote-modal-overlay" onClick={handleOverlayClick}>
      <div className="quote-modal-container">
        <div className="quote-modal-header">
          <div className="form-heading">
            <h3 className="title">Get a Quote</h3>
            <p className="sub-title">Tell us about your project and we'll get back to you shortly.</p>
          </div>
          <button className="quote-modal-close" onClick={onClose} aria-label="Close modal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="quote-modal-body">
          {isSuccess ? (
            <div className="quote-success-message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3>Thank You!</h3>
              <p>Your quote request has been submitted successfully. Our team will contact you within 24 hours.</p>
              {/* <button className="thm-btn agency-btn" onClick={resetAndClose}>
                <span className="text">Close</span>
              </button> */}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="xb-contact-input-form quote-input-form">
              <div className="row mt-none-20">
                {/* Name */}
                <div className="col-lg-6 col-md-6 mt-10">
                  <label htmlFor="quote-name">Your Name*</label>
                  <div className="xb-input-field">
                    <input
                      id="quote-name"
                      type="text"
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                    <div className="input-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <span className="text-danger">
                      {validator.message("name", formData.name, "required|alpha_space")}
                    </span>
                  </div>
                </div>

                {/* Email */}
                <div className="col-lg-6 col-md-6 mt-10">
                  <label htmlFor="quote-email">Email*</label>
                  <div className="xb-input-field">
                    <input
                      id="quote-email"
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    <div className="input-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>
                    <span className="text-danger">
                      {validator.message("email", formData.email, "required|email")}
                    </span>
                  </div>
                </div>

                {/* Phone */}
                <div className="col-lg-6 col-md-6 mt-10">
                  <label htmlFor="quote-phone">Phone*</label>
                  <div className="xb-input-field quote-phone-field">
                    <div className="quote-phone-wrapper">
                      <input
                        className="quote-country-code"
                        type="text"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                      />
                      
                      <input
                        id="quote-phone"
                        type="text"
                        name="phone"
                        placeholder="Phone number"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="input-icon phone-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.29-2.29a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <span className="text-danger">
                      {validator.message("phone", formData.phone, "required|numeric|min:10")}
                    </span>
                  </div>
                </div>

                {/* Service */}
                <div className="col-lg-6 col-md-6 mt-10">
                  <label htmlFor="quote-service">Select Service*</label>
                  <div className="xb-input-field xb-select-field">
                    <select
                      id="quote-service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="nice-select"
                    >
                      <option value="">Choose a service...</option>
                      {SERVICES.map((group) => (
                        <optgroup key={group.group} label={group.group}>
                          {group.items.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <div className="input-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                    </div>
                    <span className="text-danger">
                      {validator.message("service", formData.service, "required")}
                    </span>
                  </div>
                </div>

                {/* Budget */}
                <div className="col-lg-12 col-md-12 mt-10">
                  <label htmlFor="quote-budget">Budget Range</label>
                  <div className="xb-input-field xb-select-field">
                    <select
                      id="quote-budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="nice-select"
                    >
                      <option value="">Select your budget...</option>
                      {BUDGET_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <div className="input-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="1" x2="12" y2="23" />
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="col-lg-12 col-md-12 mt-10">
                  <label htmlFor="quote-message">Project Details</label>
                  <div className="xb-input-field xb-massage-field">
                    <textarea
                      id="quote-message"
                      name="message"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleInputChange}
                    ></textarea>
                    <div className="input-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* reCAPTCHA */}
              <div className="quote-recaptcha-wrapper">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY"}
                  onChange={handleRecaptchaChange}
                  theme="dark"
                />
              </div>

              {!recaptchaToken && validator.messagesShown && (
                <p className="text-danger text-center mb-20">
                  Please complete the reCAPTCHA verification
                </p>
              )}

              {/* Submit Button */}
              <div className="form-submit-btn mt-35">
                <button className="thm-btn agency-btn" type="submit" disabled={isSubmitting}>
                  <span className="text">{isSubmitting ? "Submitting..." : "Submit Request"}</span>
                  <span className="arrow">
                    <span className="arrow-icon">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="5.06" y="19.97" width="20.57" height="2.61" transform="rotate(-40.28 5.06 19.97)" fill="white"/>
                        <rect x="7.97" y="7.24" width="2.61" height="2.61" transform="rotate(-40.28 7.97 7.24)" fill="white"/>
                        <rect x="11.65" y="7.55" width="2.61" height="2.61" transform="rotate(-40.28 11.65 7.55)" fill="white"/>
                        <rect x="15.33" y="7.85" width="2.61" height="2.61" transform="rotate(-40.28 15.33 7.85)" fill="white"/>
                        <rect x="18.71" y="11.84" width="2.61" height="2.61" transform="rotate(-40.28 18.71 11.84)" fill="white"/>
                        <rect x="18.41" y="15.52" width="2.61" height="2.61" transform="rotate(-40.28 18.41 15.52)" fill="white"/>
                        <rect x="18.10" y="19.20" width="2.61" height="2.61" transform="rotate(-40.28 18.10 19.20)" fill="white"/>
                      </svg>
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="5.06" y="19.97" width="20.57" height="2.61" transform="rotate(-40.28 5.06 19.97)" fill="white"/>
                        <rect x="7.97" y="7.24" width="2.61" height="2.61" transform="rotate(-40.28 7.97 7.24)" fill="white"/>
                        <rect x="11.65" y="7.55" width="2.61" height="2.61" transform="rotate(-40.28 11.65 7.55)" fill="white"/>
                        <rect x="15.33" y="7.85" width="2.61" height="2.61" transform="rotate(-40.28 15.33 7.85)" fill="white"/>
                        <rect x="18.71" y="11.84" width="2.61" height="2.61" transform="rotate(-40.28 18.71 11.84)" fill="white"/>
                        <rect x="18.41" y="15.52" width="2.61" height="2.61" transform="rotate(-40.28 18.41 15.52)" fill="white"/>
                        <rect x="18.10" y="19.20" width="2.61" height="2.61" transform="rotate(-40.28 18.10 19.20)" fill="white"/>
                      </svg>
                    </span>
                  </span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuoteModal;
